import {
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  Dashboard as DashboardIcon,
  DynamicForm as DynamicFormIcon,
  EventNote as EventNoteIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  Place as StationIcon,
  FormatQuote as StatementIcon,
  Tune as ParameterIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDirection } from '../../context/DirectionContext';
import { translate as t } from '../../i18n/translations';
import LanguageSwitcher from './LanguageSwitcher';

interface SidebarProps {
  width: number;
}

const Sidebar: React.FC<SidebarProps> = ({ width }) => {
  const navigate    = useNavigate();
  const location    = useLocation();
  const { state, logout } = useAuth();
  const { lang, dir } = useDirection();

  const navItems = [
    { key: 'nav.dashboard'      as const, icon: <DashboardIcon   fontSize="small" />, path: '/dashboard' },
    { key: 'nav.users'          as const, icon: <PeopleIcon       fontSize="small" />, path: '/users' },
    { key: 'nav.cycles'         as const, icon: <EventNoteIcon    fontSize="small" />, path: '/cycles' },
    { key: 'nav.summary'        as const, icon: <AssessmentIcon   fontSize="small" />, path: '/summary' },
    { key: 'nav.formBuilder'    as const, icon: <DynamicFormIcon  fontSize="small" />, path: '/forms/builder' },
    { key: 'nav.stationBuilder' as const, icon: <StationIcon      fontSize="small" />, path: '/stations/builder' },
    { key: 'nav.statements'     as const, icon: <StatementIcon    fontSize="small" />, path: '/statements/builder' },
    { key: 'nav.parameters'     as const, icon: <ParameterIcon    fontSize="small" />, path: '/parameters' },
  ];

  const bottomNavItems = [
    { key: 'nav.unitInfo' as const, icon: <BusinessIcon fontSize="small" />, path: '/units' },
  ];

  const isActive = (path: string) => {
    if (path === '/cycles')            return location.pathname.startsWith('/cycles');
    if (path === '/forms/builder')     return location.pathname.startsWith('/forms');
    if (path === '/stations/builder')  return location.pathname.startsWith('/stations');
    if (path === '/statements/builder') return location.pathname.startsWith('/statements');
    return location.pathname === path;
  };

  const itemSx = (active: boolean) => ({
    mx: 1,
    borderRadius: 2,
    mb: 0.5,
    py: 1,
    color: active ? '#FFFFFF' : '#94A3B8',
    backgroundColor: active ? '#6366F1' : 'transparent',
    '&:hover': {
      backgroundColor: active ? '#5457D8' : '#1E293B',
      color: '#FFFFFF',
    },
    transition: 'all 0.15s ease',
  });

  return (
    <Drawer
      variant="permanent"
      anchor={dir === 'rtl' ? 'right' : 'left'}
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          backgroundColor: '#0F172A',
          borderInlineEnd: 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Brand */}
      <Box sx={{ px: 2.5, py: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: '0.8rem',
            color: '#FFF',
            letterSpacing: '-0.5px',
            flexShrink: 0,
          }}
        >
          OS
        </Box>
        <Box>
          <Typography sx={{ color: '#F8FAFC', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
            OpSelect
          </Typography>
          <Typography
            sx={{
              color: '#475569',
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            {t('nav.adminLabel', lang)}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#1E293B', mx: 2, mb: 1 }} />

      {/* Main nav */}
      <List sx={{ px: 1, pt: 1, flexGrow: 1 }}>
        {navItems.map(item => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton onClick={() => navigate(item.path)} sx={itemSx(isActive(item.path))}>
              <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={t(item.key, lang)}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Bottom nav */}
      <List sx={{ px: 1 }}>
        {bottomNavItems.map(item => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton onClick={() => navigate(item.path)} sx={itemSx(isActive(item.path))}>
              <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={t(item.key, lang)}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ borderColor: '#1E293B', mx: 2, mt: 1 }} />

      {/* Language toggle */}
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <LanguageSwitcher variant="dark" />
      </Box>

      {/* User */}
      {state.user && (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: '#6366F1',
              fontSize: '0.8rem',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {state.user.firstName?.[0]}
            {state.user.lastName?.[0]}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: '#F8FAFC',
                fontSize: '0.8rem',
                fontWeight: 600,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {state.user.firstName} {state.user.lastName}
            </Typography>
            <Typography sx={{ color: '#475569', fontSize: '0.7rem' }}>
              {state.user.roles?.[0] ?? t('nav.adminLabel', lang)}
            </Typography>
          </Box>
          <Tooltip title={t('nav.signOut', lang)} placement="top">
            <Box
              onClick={logout}
              sx={{
                color: '#475569',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                '&:hover': { color: '#F87171' },
                transition: 'color 0.15s',
                flexShrink: 0,
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
            </Box>
          </Tooltip>
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
